import React, { useState, useEffect } from "react"
import { Box, Paper, Typography, CircularProgress, Alert } from "@mui/material"
import SettingsIcon from "@mui/icons-material/Settings"
import { useTranslation } from "react-i18next"
import apiClient from "../../services/apiClient"
import OutletSelector from "../../components/settings/OutletSelector"
import PaymentMethodsSection from "../../components/settings/PaymentMethodsSection"
import DocumentCustomizationSection from "../../components/settings/DocumentCustomizationSection"
import VatRatesSection from "../../components/settings/VatRatesSection";


const SettingsPage: React.FC = () => {
    const { t } = useTranslation()
    const [companyId, setCompanyId] = useState("")
    const [outlets, setOutlets] = useState<any[]>([])
    const [selectedOutletId, setSelectedOutletId] = useState("")
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")

    // Load companyId
    useEffect(() => {
        let cancelled = false
            ; (async () => {
                try {
                    const res = await apiClient.get("/api/companies")
                    if (cancelled) return
                    const list = res.companies || []
                    if (list.length > 0) setCompanyId(list[0].id)
                } catch (e: any) {
                    if (!cancelled)
                        setError(
                            t("settings.notifications.loadCompaniesError") || ""
                        )
                }
            })()
        return () => {
            cancelled = true
        }
    }, [])

    // Load outlets for company
    useEffect(() => {
        if (!companyId) return
        let cancelled = false
            ; (async () => {
                try {
                    setLoading(true)
                    const res = await apiClient.get(
                        `/api/outlets?company_id=${companyId}`
                    )
                    if (cancelled) return
                    const list = res.outlets || []
                    setOutlets(list)
                    // Auto-select first/only outlet
                    if (list.length > 0 && !selectedOutletId) {
                        setSelectedOutletId(list[0].id)
                    }
                } catch (e: any) {
                    if (!cancelled)
                        setError(t("settings.notifications.loadOutletsError") || "")
                } finally {
                    if (!cancelled) setLoading(false)
                }
            })()
        return () => {
            cancelled = true
        }
    }, [companyId]) // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 300,
                }}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ maxWidth: 900, mx: "auto", px: 2, py: 3 }}>
            {/* Header */}
            <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
            >
                <SettingsIcon sx={{ fontSize: 28, color: "#6366f1" }} />
                <Typography variant="h5" fontWeight={700}>
                    {t("settings.title")}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Outlet Selector */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <OutletSelector
                    outlets={outlets}
                    selectedOutletId={selectedOutletId}
                    onChange={setSelectedOutletId}
                />
            </Paper>

            {selectedOutletId ? (
                <>
                    {/* Payment Methods Section */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <PaymentMethodsSection
                            companyId={companyId}
                            outletId={selectedOutletId}
                        />
                    </Paper>
                    {/* VAT Rates Section */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <VatRatesSection companyId={companyId} outletId={selectedOutletId} />
                    </Paper>
                    {/* Document Customization Section */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <DocumentCustomizationSection
                            companyId={companyId}
                            outletId={selectedOutletId}
                        />
                    </Paper>
                </>
            ) : (
                <Alert severity="info">
                    {t("settings.notifications.selectOutlet")}
                </Alert>
            )}
        </Box>
    )
}

export default SettingsPage
